/**
 * FileJSON — the structured file wire format the run door expects.
 *
 * Deliberately a standalone copy of `frontend/src/lib/file-value.ts` +
 * `read-file.ts` rather than an import: the embed bundle is built from its own
 * Vite root and ships to third-party sites, so it cannot pull in the SPA's
 * module graph. The shape is a cross-file contract — change it here and there
 * together, and keep it aligned with `app/services/agents/types.FileJSON`.
 */

/** The canonical structured format: raw base64, no `data:` prefix. */
export interface FileJSON {
  filename: string
  mime_type: string
  /** Raw base64; "" for a reference. */
  data: string
  /**
   * The stored file this value names: the widget uploads a picked file once
   * (`POST /embed/{app}/files`) and the run carries the id, not the bytes.
   */
  file_id?: string
  size_bytes?: number
}

export function isFileJSON(value: unknown): value is FileJSON {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>
  return (
    typeof obj.filename === 'string' &&
    typeof obj.mime_type === 'string' &&
    typeof obj.data === 'string'
  )
}

/**
 * Normalize any supported file value into a FileJSON, or null if it is not one.
 *
 * Accepts the legacy pipe format (`name|data:mime;base64,…`) and a bare data URL
 * as well: persisted flows and frozen snapshots carry those forever, and a
 * default value on an input field can arrive in either shape.
 */
export function parseAnyFileFormat(value: unknown): FileJSON | null {
  if (isFileJSON(value)) return value
  if (typeof value !== 'string') return null

  let filename = ''
  let dataUrlPart: string

  const pipeIndex = value.indexOf('|data:')
  if (pipeIndex > 0) {
    filename = value.substring(0, pipeIndex)
    dataUrlPart = value.substring(pipeIndex + 1)
  } else if (value.startsWith('data:')) {
    dataUrlPart = value
  } else {
    return null
  }

  // MIME types can carry parameters (audio/webm;codecs=opus), so the type stops
  // at the first ';' or ',' and the base64 payload is whatever follows.
  const match = dataUrlPart.match(/^data:([^;,]+).*?;base64,(.*)$/s)
  if (!match) return null

  return { filename, mime_type: match[1], data: match[2] }
}

export function fileJSONToDataUrl(file: FileJSON): string {
  return `data:${file.mime_type};base64,${file.data}`
}

/** Build a FileJSON from a File and the data URL a FileReader produced. */
export function createFileJSON(file: File, readerResult: string): FileJSON {
  const commaIndex = readerResult.indexOf(',')
  const base64 = commaIndex >= 0 ? readerResult.substring(commaIndex + 1) : ''

  let mimeType = file.type || 'application/octet-stream'
  if (readerResult.startsWith('data:')) {
    const semiIndex = readerResult.indexOf(';')
    if (semiIndex > 5) mimeType = readerResult.substring(5, semiIndex)
  }

  return { filename: file.name, mime_type: mimeType, data: base64 }
}

/** Approximate byte size of the decoded content. */
export function fileJSONSize(file: FileJSON): number {
  // endsWith, not /=+$/: an unanchored regex walks the whole base64 body first.
  const padding = file.data.endsWith('==') ? 2 : file.data.endsWith('=') ? 1 : 0
  return Math.floor((file.data.length * 3) / 4) - padding
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** A read failure whose `message` is ready to show the user, naming the file. */
export class FileReadError extends Error {
  readonly filename: string
  constructor(message: string, filename: string) {
    super(message)
    this.name = 'FileReadError'
    this.filename = filename
  }
}

const READ_RETRY_DELAY_MS = 400

function readOnce(file: File | Blob, name: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      typeof reader.result === 'string'
        ? resolve(reader.result)
        : reject(reader.error ?? new Error(`Uventet format for "${name}"`))
    reader.onerror = () => reject(reader.error ?? new Error('Ukendt fejl'))
    reader.readAsDataURL(file)
  })
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Read a file as a data URL, retrying a `NotReadableError` once.
 *
 * That error means the OS refused the read — the file is open in another
 * program, or it is an un-downloaded cloud placeholder (iCloud/OneDrive). The
 * first attempt can itself trigger the download, so one retry often succeeds;
 * if it doesn't, the message says what the user can actually do about it.
 */
export async function readFileAsDataURL(file: File): Promise<string> {
  try {
    return await readOnce(file, file.name)
  } catch (err) {
    if (err instanceof DOMException && err.name === 'NotReadableError') {
      await delay(READ_RETRY_DELAY_MS)
      try {
        return await readOnce(file, file.name)
      } catch {
        throw new FileReadError(
          `Filen "${file.name}" kunne ikke læses. Den er måske åben i et andet ` +
            `program, eller browseren mangler adgang til den. Luk filen andre ` +
            `steder, eller hent en lokal kopi, og prøv igen.`,
          file.name
        )
      }
    }
    const detail = err instanceof Error ? err.message : 'Ukendt fejl'
    throw new FileReadError(`Filen "${file.name}" kunne ikke læses: ${detail}`, file.name)
  }
}

/** Read a file into a FileJSON, refusing anything over `maxSizeMB` first. */
export async function fileToFileJSON(file: File, maxSizeMB?: number | null): Promise<FileJSON> {
  if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
    throw new FileReadError(
      `Filen "${file.name}" fylder ${formatBytes(file.size)} og overstiger grænsen på ${maxSizeMB} MB.`,
      file.name
    )
  }
  return createFileJSON(file, await readFileAsDataURL(file))
}
